import React from 'react';
import { FileGroup } from '../types';
import { formatBytes, formatDate, downloadBase64 } from '../utils';
import { compareOcrResults } from '../ocrComparison';
import { RunCard } from './RunCard';

interface FileCardProps {
  group: FileGroup;
  expandedFiles: Set<string>;
  expandedRuns: Set<string>;
  isPinned: boolean;
  onToggleFile: () => void;
  onToggleRun: (runId: string) => void;
  onTogglePin: () => void;
  onRemoveRun: (id: string) => void;
  onRemoveGroup: () => void;
  onRerun: (fileGroupId: string) => void;
}

export function FileCard({
  group, expandedFiles, expandedRuns, isPinned,
  onToggleFile, onToggleRun, onTogglePin, onRemoveRun, onRemoveGroup, onRerun,
}: FileCardProps) {
  const expanded = expandedFiles.has(group.fileGroupId);
  const latestRun = group.runs[0];
  const ext = latestRun.fileName.split('.').pop()?.toUpperCase() || 'FILE';

  const bothSucceeded = !latestRun.documentAi.error && !latestRun.deapOcr.error
    && latestRun.documentAi.processed && latestRun.deapOcr.raw;
  const comparison = bothSucceeded
    ? compareOcrResults(latestRun.documentAi.processed, latestRun.deapOcr.raw)
    : null;

  const matchBadgeClass = comparison
    ? comparison.matchPercentage >= 90 ? 'high'
      : comparison.matchPercentage >= 70 ? 'medium'
      : 'low'
    : '';

  return (
    <div className={`result-card ${isPinned ? 'pinned' : ''}`}>
      <div className="card-header" onClick={onToggleFile}>
        <div className="card-header-left">
          <svg
            className={`chevron ${expanded ? 'open' : ''}`}
            width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="#484F58" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
          <div className="card-file-info">
            <span className="card-filename">{latestRun.fileName}</span>
            <span className="card-badge">{ext}</span>
            <span className="card-meta">
              {formatBytes(latestRun.fileSize)}  ·  {formatDate(latestRun.timestamp)}
            </span>
            {group.runs.length > 1 && (
              <span className="run-count-badge">{group.runs.length} runs</span>
            )}
          </div>
        </div>
        <div className="card-actions" onClick={e => e.stopPropagation()}>
          {comparison && (
            <span className={`match-badge ${matchBadgeClass}`}>
              Match {comparison.matchPercentage.toFixed(0)}%
            </span>
          )}
          {latestRun.fileBlob && (
            <button
              className="btn-ghost"
              onClick={() => downloadBase64(latestRun.fileBlob, latestRun.fileName, latestRun.mimeType)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#30363D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span>Download</span>
            </button>
          )}
          {latestRun.fileBlob && (
            <button className="btn-ghost" onClick={() => onRerun(group.fileGroupId)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8B949E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.5 2v6h-6" /><path d="M21.34 15.57a10 10 0 1 1-.57-8.38L21.5 8" />
              </svg>
            </button>
          )}
          <button
            className={`btn-pin ${isPinned ? 'pinned' : ''}`}
            onClick={onTogglePin}
            title={isPinned ? 'Odepnout' : 'Pripnout (zachovat po refreshi)'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24"
              fill={isPinned ? '#58A6FF' : 'none'}
              stroke={isPinned ? '#58A6FF' : '#8B949E'}
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
              <path d="M12 17v5" />
              <path d="M9 2.8l6 0l-1 6.2l3.5 0l-5.5 8l-5.5-8l3.5 0z" />
            </svg>
          </button>
          <button className="btn-trash" onClick={onRemoveGroup}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F85149" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
            </svg>
          </button>
        </div>
      </div>

      {expanded && (
        <div className="file-card-body">
          {group.runs.map(run => (
            <RunCard
              key={run.id}
              result={run}
              expanded={expandedRuns.has(run.id)}
              onToggle={() => onToggleRun(run.id)}
              onRemove={group.runs.length > 1 ? () => onRemoveRun(run.id) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}
